// CockpitMainNav - Menu de navigation principal pour les Cockpits Desktop
// Affiche: Accueil | Artisans | Acheter à un pro | Entre particuliers | Réseau | Emploi | Espace Pro
// Sous-menus HORIZONTAUX - Navigation INTERNE au cockpit

import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Users, Store, Network, Briefcase, LayoutDashboard,
  ShoppingBag, Wrench, Zap, TrendingUp, Sparkles, Menu, SlidersHorizontal,
  Search, ClipboardList, Home, Clock, AlertTriangle, PlusCircle,
  Video, Image as ImageIcon, MessageCircle, Calendar, Building2, MapPin
} from 'lucide-react';

// Navigation principale - avec contextes pour la navigation interne
const NAV_ITEMS = [
  { context: 'page_accueil_bati', label: 'Accueil', icon: Home },
  { context: 'page_artisans', label: 'Artisans', icon: Users },
  { context: 'page_marketplace', label: 'Acheter à un pro', icon: Store },
  { context: 'page_entre_particuliers', label: 'Entre particuliers', icon: Users },
  { context: 'page_reseau', label: 'Réseau', icon: Network },
  { context: 'page_emploi', label: 'Emploi', icon: Briefcase },
  { context: 'page_espace_pro', label: 'Espace Pro', icon: LayoutDashboard },
];

// Sous-menus EXACTS comme MainNavigation.jsx
const SUBMENUS = {
  'page_artisans': [
    { context: 'page_artisans', label: 'Trouve un artisan en 3 clics', icon: Search, isArtisansFilterButton: true },
    { context: 'page_decrire_projet', label: 'Reçois jusqu\'à 5 devis gratuits', icon: ClipboardList, isDevisButton: true },
    { context: 'page_artisans_carte', label: 'Carte', icon: Building2, isCarteButton: true },
  ],
  'page_marketplace': [
    { context: null, label: 'Rayons', icon: Menu, isRayonsButton: true },
    { context: null, label: 'Trouve en 3 clics', icon: SlidersHorizontal, isFilterButton: true },
    { context: 'page_ventes_flash', label: 'Ventes Flash', icon: Zap },
    { context: 'page_meilleures_ventes', label: 'Meilleures ventes', icon: TrendingUp },
    { context: 'page_nouveautes', label: 'Nouveautés', icon: Sparkles },
  ],
  'page_entre_particuliers': [
    { context: 'page_entre_particuliers', label: 'Accueil', icon: Home },
    { context: null, label: 'Catégories', icon: Menu, isCategoriesButton: true },
    { context: null, label: 'Trouve ton annonce en 3 clics', icon: SlidersHorizontal, isParticuliersFilterButton: true },
    { context: 'page_annonces_recentes', label: 'Annonces récentes', icon: Clock },
    { context: 'page_annonces_urgentes', label: 'Urgent', icon: AlertTriangle },
    { context: 'page_deposer_annonce', label: 'Déposer une annonce', icon: PlusCircle },
  ],
  'page_reseau': [
    { context: 'page_reseau', label: "Fil d'actualité", icon: Home },
    { context: 'page_reseau_cinema', label: 'Cinéma', icon: Video },
    { context: 'page_reseau_galerie', label: 'Galerie', icon: ImageIcon },
    { context: 'page_reseau_salon', label: 'Salon', icon: MessageCircle },
    { context: 'page_reseau_evenements', label: 'Événement', icon: Calendar },
  ],
  'page_emploi': [
    { context: 'page_emploi', label: 'Toutes les offres BTP', icon: Home },
    { context: null, label: 'Trouve ton emploi en 3 clics', icon: SlidersHorizontal, isEmploiFilterButton: true },
    { context: 'page_emploi_entreprises', label: 'Entreprises qui recrutent', icon: Building2 },
  ],
};

export default function CockpitMainNav({ 
  onNavigate, 
  activeContext,
  onOpenRayonsMenu, 
  onOpenFilters, 
  onOpenArtisansFilters,
  onOpenParticuliersCategories, 
  onOpenParticuliersFilters,
  onOpenEmploiFilters
}) {
  const location = useLocation();
  const [hoveredSection, setHoveredSection] = useState(null);

  // Déterminer si un item du menu principal est actif
  const isMainItemActive = (context) => {
    if (!activeContext) return false;
    // Vérifier si le contexte actif correspond ou est un sous-élément
    if (activeContext === context) return true;
    if (activeContext.startsWith(context.replace('page_', 'page_'))) return true;
    // Mapping spécifique
    if (context === 'page_marketplace' && (
      activeContext.startsWith('page_produits') || 
      activeContext.startsWith('page_prestations') ||
      activeContext.startsWith('page_ventes') ||
      activeContext.startsWith('page_meilleures') ||
      activeContext.startsWith('page_nouveautes')
    )) return true;
    return false;
  };

  // Déterminer quelle section afficher le sous-menu
  const getActiveSubmenuKey = () => {
    if (hoveredSection && SUBMENUS[hoveredSection]) return hoveredSection;
    // Basé sur le contexte actif
    for (const key of Object.keys(SUBMENUS)) {
      if (isMainItemActive(key)) return key;
    }
    return null;
  };

  const activeSubmenuKey = getActiveSubmenuKey();
  const activeSubmenu = activeSubmenuKey ? SUBMENUS[activeSubmenuKey] : null;

  // Rendu d'un item de sous-menu
  const renderSubmenuItem = (subItem, idx) => {
    const SubIcon = subItem.icon;
    const isSubActive = subItem.context && activeContext === subItem.context;

    // Bouton Rayons (orange)
    if (subItem.isRayonsButton) {
      return (
        <button
          key="rayons-btn"
          onClick={() => onOpenRayonsMenu?.()}
          className="flex items-center gap-2 px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium rounded-lg transition-colors whitespace-nowrap"
          data-testid="submenu-rayons"
        >
          {SubIcon && <SubIcon size={16} />}
          <span>{subItem.label}</span>
        </button>
      );
    }

    // Bouton Catégories (Entre particuliers)
    if (subItem.isCategoriesButton) {
      return (
        <button
          key="categories-btn"
          onClick={() => onOpenParticuliersCategories?.()}
          className="flex items-center gap-2 px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium rounded-lg transition-colors whitespace-nowrap"
          data-testid="submenu-categories-particuliers"
        >
          {SubIcon && <SubIcon size={16} />}
          <span>{subItem.label}</span>
        </button>
      );
    }

    // Bouton Filtre Artisans (fond bleu - Trouver)
    if (subItem.isArtisansFilterButton) {
      return (
        <button
          key="artisans-filter-btn"
          onClick={() => onOpenArtisansFilters?.()}
          className="flex items-center gap-2 px-4 py-2 bg-[#E0F2FE] hover:bg-[#BAE6FD] text-[#0C4A6E] font-bold rounded-lg transition-colors whitespace-nowrap border-2 border-[#0369A1]"
          data-testid="submenu-artisans-filter"
        >
          {SubIcon && <SubIcon size={16} />}
          <span>{subItem.label}</span>
        </button>
      );
    }

    // Boutons "Trouve en 3 clics" (fond bleu)
    if (subItem.isFilterButton || subItem.isParticuliersFilterButton || subItem.isEmploiFilterButton) {
      return (
        <button
          key={`filter-btn-${idx}`}
          onClick={() => {
            if (subItem.isParticuliersFilterButton) {
              onOpenParticuliersFilters?.();
            } else if (subItem.isEmploiFilterButton) {
              onOpenEmploiFilters?.();
            } else {
              onOpenFilters?.();
            }
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[#E0F2FE] hover:bg-[#BAE6FD] text-[#0C4A6E] font-bold rounded-lg transition-colors whitespace-nowrap border-2 border-[#0369A1]"
          data-testid={`submenu-filter-${idx}`}
        >
          {SubIcon && <SubIcon size={16} />}
          <span>{subItem.label}</span>
        </button>
      );
    }

    // Bouton Devis (bleu marine)
    if (subItem.isDevisButton) {
      return (
        <button
          key="devis-btn"
          onClick={() => onNavigate?.(subItem.context, subItem.label)}
          className="flex items-center gap-2 px-4 py-2 bg-[#1E3A5F] hover:bg-[#152a45] text-white font-medium rounded-lg transition-colors whitespace-nowrap"
          data-testid="submenu-devis"
        >
          {SubIcon && <SubIcon size={16} />}
          <span>{subItem.label}</span>
        </button>
      );
    }

    // Bouton Carte (vert émeraude)
    if (subItem.isCarteButton) {
      return (
        <button
          key="carte-btn"
          onClick={() => onNavigate?.(subItem.context, subItem.label)}
          className="flex items-center gap-2 px-4 py-2 bg-[#10B981] hover:bg-[#059669] text-white font-medium rounded-lg transition-colors whitespace-nowrap"
          data-testid="submenu-carte-artisans"
        >
          <MapPin size={16} />
          <span>{subItem.label}</span>
        </button>
      );
    }

    // Item "En cours de développement"
    if (subItem.isComingSoon) {
      return (
        <div key={`coming-soon-${idx}`} className="relative group">
          <button
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-400 font-medium rounded-lg cursor-not-allowed whitespace-nowrap"
            disabled
          >
            {SubIcon && <SubIcon size={16} />}
            <span>{subItem.label}</span>
          </button>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1E3A5F] text-white text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg z-50">
            En cours de développement
          </div>
        </div>
      );
    }

    // Items normaux (navigation interne)
    return (
      <button
        key={subItem.context || `item-${idx}`}
        onClick={() => onNavigate?.(subItem.context, subItem.label)}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
          isSubActive 
            ? 'bg-[#F97316]/20 text-[#F97316]' 
            : 'text-[#1E3A5F] hover:bg-orange-100/50'
        }`}
        data-testid={`submenu-${idx}`}
      >
        {SubIcon && <SubIcon size={16} className={isSubActive ? 'text-[#F97316]' : 'text-[#1E3A5F]/70'} />}
        <span>{subItem.label}</span>
      </button>
    );
  };

  return (
    <div className="hidden lg:block" data-testid="cockpit-main-nav">
      {/* ═══ MENU PRINCIPAL - Fond orange doux ═══ */}
      <nav className="bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100">
        <div className="px-6">
          <div className="flex items-center gap-1">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = isMainItemActive(item.context);
              const hasSubmenu = !!SUBMENUS[item.context];

              return (
                <button
                  key={item.context}
                  onClick={() => onNavigate?.(item.context, item.label)}
                  onMouseEnter={() => hasSubmenu && setHoveredSection(item.context)}
                  onMouseLeave={() => setHoveredSection(null)}
                  className={`
                    flex items-center gap-1.5 px-3 py-3 text-sm font-medium transition-colors relative
                    ${active 
                      ? 'text-[#E8650A]' 
                      : 'text-[#1E3A5F] hover:text-[#E8650A]'
                    }
                  `}
                >
                  <Icon size={16} />
                  <span>{item.label}</span>
                  {active && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#E8650A] rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* ═══ SOUS-MENU HORIZONTAL - Fond orange doux ═══ */}
      {activeSubmenu && (
        <div 
          className="bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100"
          onMouseEnter={() => setHoveredSection(activeSubmenuKey)}
          onMouseLeave={() => setHoveredSection(null)}
        >
          <div className="px-6">
            <div className="flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide">
              {activeSubmenu.map((subItem, idx) => renderSubmenuItem(subItem, idx))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
